import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Route, Switch, useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import MessageEventModal from "go-component/components/MessageEvent/MessageEventModal";
import { FormatDate } from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { ReactComponent as StockDangerSVG } from "../../../../../../../../../../images/svg/documents/stock-danger.svg";
import { ReactComponent as StockSVG } from "../../../../../../../../../../images/svg/documents/stock.svg";
import { PurchaseStatus } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import CopyDocumentModal from "../../../../components/CopyDocumentModal";
import DocumentPdfGeneratorModal from "../../../../components/DocumentPdfGeneratorModal";
import { exportDocumentPdf } from "../../../../utils/documentPdf/DocumentPdf";
import { DocumentPdfEntity } from "../../../../utils/documentPdf/types";
import { PurchaseContext } from "../../pages/Preview";
import SubmenuPurchasePreviewNavigation from "../SubmenuPurchasePreviewNavigation";
import PurchaseCorrectionsTab from "../Tabs/PurchaseCorrectionsTab";
import PurchasePreviewTab from "../Tabs/PurchasePreviewTab";

interface Props {
	url: string;
}

const PurchasePreview = ({ url }: Props) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { addFlash, addSuccessFlash } = useFlash();
	const params = { include: "all" };
	const history = useHistory();
	const confirmation = useConfirmation();
	const [showLogs, setShowLogs] = useState<boolean>(false);
	const { setPurchase, purchase, fetchPurchaseReceives, customFieldsConfig, purchaseReceives } =
		useContext(PurchaseContext);
	const [showCopyModal, setShowCopyModal] = useState(false);
	const { isMobile } = useWindowSize();
	const [showPdfGeneratorModal, setShowPdfGeneratorModal] = useState(false);
	const hasLockPermission = useSelector(hasPermission(["PURCHASE_ORDER_MANAGE", "PURCHASE_ORDER_LOCK"]));
	const hasRemovePermission = useSelector(hasPermission(["PURCHASE_ORDER_MANAGE", "PURCHASE_ORDER_DELETE"]));

	const renderTitle = () => (
		<>
			{t("modules.purchase.header.preview.title")} {purchase.document_number.replace(" ", "- ")}
			<AppFormatResourceStatusLabel status={purchase.status} />
			{purchase.lock_status === "LOCKED" && <AppFormatResourceStatusLabel status={purchase.lock_status} />}
		</>
	);

	const handleConfirmPurchase = async () => {
		try {
			const res = await api.organization().confirmPurchase(purchase.id, params);
			setPurchase(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleBlockPurchase = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().blockPurchase(purchase.id, params);
			setPurchase(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleUnlockPurchase = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().unlockPurchase(purchase.id, params);
			setPurchase(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCollectPurchase = async () => {
		try {
			const res = await api.organization().collectPurchase(purchase.id, params);
			setPurchase(res);
			await fetchPurchaseReceives();

			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleVoidPurchase = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().removePurchase(purchase.id);
			setPurchase(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRemovePurchase = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await api.organization().removePurchase(purchase.id);
			addSuccessFlash(t("lib:common.flash.removed"));
			history.push(`/${organization.id}/documents/purchases`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCancelPurchase = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().cancelPurchase(purchase.id, params);
			setPurchase(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRestorePurchase = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.activate", { ns: "lib" }),
			});
			const res = await api.organization().restorePurchase(purchase.id, params);
			setPurchase(res);
			addSuccessFlash(t("lib:common.flash.restored"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleExportPurchaseEInvoice = async () => {
		try {
			const res = await api.organization().exportPurchaseEInvoice(purchase.id);
			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${t(`common.word.e_invoice`)}_${purchase.document_number}.xml`);
			document.body.appendChild(link);
			link.click();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleCancelPurchaseReceive = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			const res = await api.organization().cancelPurchaseReceive(purchase.id, params);
			setPurchase(res);
			await fetchPurchaseReceives();

			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handlePrint = () => {
		return exportDocumentPdf(
			{
				company: organization.company_name,
				organization: organization.name,
				taxIdNo: organization.tax_id_no,
				printCustomFields: true,
				printPrices: true,
			},
			DocumentPdfEntity.PURCHASE,
			t,
			purchase.items,
			purchase,
			customFieldsConfig ?? [],
			purchaseReceives ? purchaseReceives[0]?.received_at : undefined,
			true
		);
	};

	const handleExport = () => {
		return exportDocumentPdf(
			{
				company: organization.company_name,
				organization: organization.name,
				taxIdNo: organization.tax_id_no,
				printCustomFields: true,
				printPrices: true,
			},
			DocumentPdfEntity.PURCHASE,
			t,
			purchase.items,
			purchase,
			customFieldsConfig ?? [],
			purchaseReceives ? purchaseReceives[0]?.received_at : undefined
		);
	};

	const buttons: ButtonProps[] = [
		{
			title: t("lib:common.action.edit"),
			path: `/${organization.id}/documents/purchases/${purchase.id}/edit`,
			variant: "primary",
			hide: purchase.lock_status === "LOCKED",
		},
		{
			title: t("lib:common.action.confirm"),
			action: handleConfirmPurchase,
			variant: "light",
			dropdown: false,
			hide: purchase.status !== PurchaseStatus.DRAFT,
		},
		{
			title: t("lib:common.action.remove"),
			action: handleRemovePurchase,
			variant: "light",
			dropdown: false,
			hide: !(
				(purchase.status === PurchaseStatus.DRAFT || purchase.status === PurchaseStatus.VOID) &&
				hasRemovePermission
			),
		},
		{
			title: t("modules.purchase.actions.lock.title"),
			action: handleBlockPurchase,
			dropdown: true,
			hide: !(purchase.lock_status === "UNLOCKED" && hasLockPermission),
		},
		{
			title: t("modules.purchase.actions.unlock.title"),
			action: handleUnlockPurchase,
			dropdown: true,
			hide: !(purchase.lock_status === "LOCKED" && hasLockPermission),
		},
		{
			title: t("common.action.export_e_invoice"),
			action: handleExportPurchaseEInvoice,
			dropdown: true,
		},
		{
			title: t("lib:common.action.copy"),
			action: () => setShowCopyModal(true),
			dropdown: true,
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(!showLogs),
			dropdown: true,
		},
		{
			title: t("modules.purchase.action.collect.title"),
			action: handleCollectPurchase,
			variant: "primary",
			hide: purchase.status !== PurchaseStatus.OPEN,
		},
		{
			title: t("lib:common.action.cancel"),
			action: handleCancelPurchase,
			dropdown: true,
			hide: purchase.status !== PurchaseStatus.OPEN,
		},
		{
			title: t("common.action.void"),
			action: handleVoidPurchase,
			dropdown: true,
			hide: !(purchase.status !== PurchaseStatus.DRAFT && purchase.status !== PurchaseStatus.VOID),
		},
		{
			title: t("lib:common.action.restore"),
			action: handleRestorePurchase,
			dropdown: true,
			hide: !(purchase.status === PurchaseStatus.VOID && hasRemovePermission),
		},
		{
			title: t("modules.purchase.action.cancel_receive.title"),
			action: handleCancelPurchaseReceive,
			variant: "primary",
			hide: !(purchase.status === "RECEIVED" || purchase.status === "PARTIALLY_RECEIVED"),
		},
		{
			title: t("modules.document.action.generate_pdf.title"),
			action: () => setShowPdfGeneratorModal(true),
			dropdown: true,
		},
		{
			title: t("lib:common.action.print"),
			action: handlePrint,
			dropdown: true,
		},
		{
			title: t("lib:common.action.export_pdf"),
			action: handleExport,
			dropdown: true,
		},
	];

	const avatar = {
		color: "#FF9900",
		date: purchase.purchased_at,
	};

	const drawStockName = () => {
		if (purchase?.stock?.name) return purchase.stock.name;
		if (purchase?.external_stock_name) return t("modules.purchase.field.unconnected_stock.title");
		return (
			<span className="text-danger">{t("modules.purchase.field.unconnected_stock_with_direction.title")}</span>
		);
	};

	const renderSubtitle = () => (
		<ul>
			<li>{FormatDate(purchase.purchased_at)}</li>
			<li>{FormatMoney(purchase.total_price_net_money)}</li>
			<li>
				{purchase?.stock?.name ? <StockSVG className="me-1" /> : <StockDangerSVG className="me-1" />}
				{drawStockName()}
			</li>
		</ul>
	);

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.export_e_invoice"),
			action: handleExportPurchaseEInvoice,
		},
		{
			title: t("lib:common.action.copy"),
			action: () => setShowCopyModal(true),
		},
		{
			title: t("lib:common.word.logs"),
			action: () => setShowLogs(!showLogs),
		},
		{
			title: t("lib:common.action.edit"),
			path: `/${organization.id}/documents/purchases/${purchase.id}/edit`,
			hide: purchase.lock_status === "LOCKED",
		},
		{
			title: t("lib:common.action.confirm"),
			action: handleConfirmPurchase,
			hide: purchase.status !== PurchaseStatus.DRAFT,
		},
		{
			title: t("lib:common.action.remove"),
			action: handleRemovePurchase,
			hide: !(
				(purchase.status === PurchaseStatus.DRAFT || purchase.status === PurchaseStatus.VOID) &&
				hasRemovePermission
			),
		},
		{
			title: t("modules.purchase.actions.lock.title"),
			action: handleBlockPurchase,
			hide: !(purchase.lock_status === "UNLOCKED" && hasLockPermission),
		},
		{
			title: t("modules.purchase.actions.unlock.title"),
			action: handleUnlockPurchase,
			hide: !(purchase.lock_status === "LOCKED" && hasLockPermission),
		},
		{
			title: t("modules.purchase.action.collect.title"),
			action: handleCollectPurchase,
			hide: purchase.status !== PurchaseStatus.OPEN,
		},
		{
			title: t("lib:common.action.cancel"),
			action: handleCancelPurchase,
			hide: purchase.status !== PurchaseStatus.OPEN,
		},
		{
			title: t("common.action.void"),
			action: handleVoidPurchase,
			hide: purchase.status === PurchaseStatus.DRAFT || purchase.status === PurchaseStatus.VOID,
		},
		{
			title: t("lib:common.action.restore"),
			action: handleRestorePurchase,
			hide: !(purchase.status === PurchaseStatus.VOID && hasRemovePermission),
		},
		{
			title: t("modules.purchase.action.cancel_receive.title"),
			action: handleCancelPurchaseReceive,
			hide: purchase.status !== "RECEIVED" && purchase.status !== "PARTIALLY_RECEIVED",
		},
		{
			title: t("modules.document.action.generate_pdf.title"),
			action: () => setShowPdfGeneratorModal(true),
		},
		{
			title: t("lib:common.action.print"),
			action: handlePrint,
		},
		{
			title: t("lib:common.action.export_pdf"),
			action: handleExport,
		},
	];

	return (
		<div className="content-preview">
			<MobileActions actions={mobileActions} />
			<Header
				title={renderTitle()}
				buttons={isMobile ? [] : buttons}
				avatar={avatar}
				subtitle={renderSubtitle()}
				tags={purchase?.tags?.map((tag) => tag.name)}
			/>
			<div className="content-preview-body">
				<SubmenuPurchasePreviewNavigation purchase={purchase} />
				<Switch>
					{/* <Route path={`${url}/transactions`} exact component={OrganizationTransactionIndexPage} /> */}
					<Route path={`${url}/corrections`} component={PurchaseCorrectionsTab} />
					<Route path="" exact component={PurchasePreviewTab} />
				</Switch>
			</div>
			{showLogs && (
				<MessageEventModal
					path={`/${organization.id}/logs/message_events`}
					resourceId={purchase.id}
					resourceType="PURCHASE_ORDER"
					onHide={() => setShowLogs(!showLogs)}
					organizationId={organization.id}
				/>
			)}
			{showCopyModal && (
				<CopyDocumentModal
					show={showCopyModal}
					onHide={() => setShowCopyModal(false)}
					document={purchase}
					type="purchase"
				/>
			)}
			{showPdfGeneratorModal && (
				<DocumentPdfGeneratorModal
					show={showPdfGeneratorModal}
					onHide={() => setShowPdfGeneratorModal(false)}
					entity={DocumentPdfEntity.PURCHASE}
					document={purchase}
					customFieldsConfig={customFieldsConfig || []}
					additionalDate={purchaseReceives ? purchaseReceives[0]?.received_at : undefined}
				/>
			)}
		</div>
	);
};

export default PurchasePreview;
